/**
 * Estado único de tema (modo + resolvido) compartilhado por ThemeLoader, useTheme e toggles.
 */
import { create } from 'zustand'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { themeRepository } from '@/shared/repositories/settings/themeRepository'
import {
  markThemeUserChoice,
  persistThemeModeLocal,
  readBootThemeMode,
  resolveThemeModeAfterServer,
} from './themePreference'
import {
  applyTheme,
  getSystemTheme,
  resolveTheme,
  type ResolvedTheme,
  type ThemeMode,
} from './themeUtils'

type ThemeState = {
  mode: ThemeMode
  resolved: ResolvedTheme
  ready: boolean
  hydratedForUserId: string | null
  applyMode: (mode: ThemeMode, opts?: { persistLocal?: boolean; fromUser?: boolean; fromServer?: boolean }) => void
  setMode: (mode: ThemeMode) => Promise<void>
  hydrateForUser: (userId: string) => Promise<void>
  hydrateForFixtures: () => void
}

let mediaListenerAttached = false
let hydrateGeneration = 0
let userThemeGeneration = 0

export const resetThemeStoreInternalsForTests = (): void => {
  mediaListenerAttached = false
  hydrateGeneration = 0
  userThemeGeneration = 0
}

const attachSystemThemeListener = (getMode: () => ThemeMode, setResolved: (r: ResolvedTheme) => void) => {
  if (mediaListenerAttached || typeof window === 'undefined') return
  mediaListenerAttached = true
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    if (getMode() === 'system') {
      const r = getSystemTheme()
      setResolved(r)
      applyTheme(r)
    }
  }
  media.addEventListener('change', handler)
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: 'system',
  resolved: typeof window !== 'undefined' ? getSystemTheme() : 'light',
  ready: false,
  hydratedForUserId: null,

  applyMode: (mode, opts = {}) => {
    const { persistLocal = true, fromUser = false, fromServer = false } = opts
    const resolved = resolveTheme(mode)
    set({ mode, resolved })
    applyTheme(resolved)
    if (persistLocal) {
      persistThemeModeLocal(mode, fromUser || fromServer)
      if (fromServer) markThemeUserChoice()
    }
  },

  setMode: async (next) => {
    userThemeGeneration += 1
    get().applyMode(next, { persistLocal: true, fromUser: true })
    if (isScreenshotFixtureMode()) return
    try {
      await themeRepository.updateTheme(next)
      markThemeUserChoice()
    } catch {
      /* local já persistido */
    }
  },

  hydrateForFixtures: () => {
    const mode = readBootThemeMode()
    get().applyMode(mode, { persistLocal: false })
    set({ ready: true })
    attachSystemThemeListener(() => get().mode, (r) => set({ resolved: r }))
  },

  hydrateForUser: async (userId) => {
    if (get().hydratedForUserId === userId && get().ready) return
    const generation = ++hydrateGeneration
    const userGenAtStart = userThemeGeneration
    attachSystemThemeListener(() => get().mode, (r) => set({ resolved: r }))

    if (isScreenshotFixtureMode()) {
      get().hydrateForFixtures()
      set({ hydratedForUserId: userId })
      return
    }

    const applyIfCurrent = (mode: ThemeMode, opts: { persistLocal?: boolean; fromServer?: boolean }) => {
      if (generation !== hydrateGeneration) return
      if (userThemeGeneration > userGenAtStart) return
      get().applyMode(mode, opts)
    }

    const result = await themeRepository.getTheme()
    const discardServerTheme =
      generation !== hydrateGeneration || userThemeGeneration > userGenAtStart

    if (!discardServerTheme) {
      if (result.status === 'error') {
        applyIfCurrent(readBootThemeMode(), { persistLocal: false })
      } else {
        const mode = resolveThemeModeAfterServer(result.theme)
        applyIfCurrent(mode, {
          fromServer: result.theme !== null,
          persistLocal: result.theme !== null,
        })
      }
    }

    if (generation === hydrateGeneration) {
      set({ ready: true, hydratedForUserId: userId })
    }
  },
}))

/**
 * Boot (antes do login): aplica o modo salvo sem regravar e já escuta o SO,
 * para "sistema" acompanhar a troca claro/escuro também na tela de login.
 */
export const initThemeFromBoot = (): void => {
  const { applyMode } = useThemeStore.getState()
  applyMode(readBootThemeMode(), { persistLocal: false })
  attachSystemThemeListener(
    () => useThemeStore.getState().mode,
    (r) => useThemeStore.setState({ resolved: r }),
  )
}
